import { useCallback, useState } from 'react'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import buildUrl from 'modules/core/lib/buildUrl'
import app from 'modules/core/store/models/app'

import useBulkUpdateFolderDocument from 'modules/documents/hooks/documents/useBulkUpdateFolderDocument'
import useMoveToTrashBinDocument from 'modules/documents/hooks/documents/useMoveToTrashBinDocument'
import useToggleVisibilityDocument from 'modules/documents/hooks/documents/useToggleVisibilityDocument'

import TrashBinModal from 'modules/documents/components/BulkActionsDatatable/TrashBinModal'
import UpdateFolderModal from 'modules/documents/components/BulkActionsDatatable/UpdateFolderModal'

import type { DocumentNode } from './types'

const ACTION_IDS = {
  TOGGLE_VISIBILITY: 'toggle_visibility',
  MOVE_TO_FOLDER: 'move_to_folder',
  DOWNLOAD: 'download',
  MOVE_TO_TRASH: 'move_to_trash',
} as const

type ActionId = (typeof ACTION_IDS)[keyof typeof ACTION_IDS]

export function useItemActions({ space }: { space: string }) {
  const [trashModalDoc, setTrashModalDoc] = useState<DocumentNode | null>(null)
  const [folderModalDoc, setFolderModalDoc] = useState<DocumentNode | null>(null)

  const { moveDocumentToTrashBin, loading: loadingTrashBin } = useMoveToTrashBinDocument({
    fromDataTable: true,
  })
  const { bulkUpdateFolder, loading: loadingUpdateFolder } = useBulkUpdateFolderDocument({
    fromDataTable: true,
  })
  const { toggleVisibilityDocument } = useToggleVisibilityDocument()

  const isInternalSpace = space === 'company_internal'

  const handleAction = useCallback(
    (actionId: string, document: DocumentNode) => {
      switch (actionId) {
        case ACTION_IDS.TOGGLE_VISIBILITY: {
          toggleVisibilityDocument({
            id: document.id,
            sendNotifications: false,
          }).then((result) => {
            const handled = handleResult(result, () => {
              app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
            })
            if (handled.success) {
              app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
            }
          })
          break
        }
        case ACTION_IDS.MOVE_TO_FOLDER:
          setFolderModalDoc(document)
          break
        case ACTION_IDS.DOWNLOAD:
          if (document.file) window.open(buildUrl(document.file), '_blank', 'noopener,noreferrer')
          break
        case ACTION_IDS.MOVE_TO_TRASH:
          setTrashModalDoc(document)
          break
      }
    },
    [toggleVisibilityDocument]
  )

  const getItemActions = useCallback(
    (document: DocumentNode) => {
      const actions: Array<{
        id: ActionId
        label: string
        onClick: () => void
        critical?: boolean
      }> = []

      const canEdit = document._policies?.edit?.can ?? false
      const canDelete = document._policies?.delete?.can ?? false

      // Visibility toggle (not available for internal/management documents)
      if (canEdit && !isInternalSpace) {
        actions.push({
          id: ACTION_IDS.TOGGLE_VISIBILITY,
          label: document.public
            ? i18n.t('documents.employee.bulk-actions.hide')
            : i18n.t('documents.employee.bulk-actions.visible'),
          onClick: () => handleAction(ACTION_IDS.TOGGLE_VISIBILITY, document),
        })
      }

      // Move to folder
      if (canEdit) {
        actions.push({
          id: ACTION_IDS.MOVE_TO_FOLDER,
          label: i18n.t('documents.move'),
          onClick: () => handleAction(ACTION_IDS.MOVE_TO_FOLDER, document),
        })
      }

      // Download
      actions.push({
        id: ACTION_IDS.DOWNLOAD,
        label: i18n.t('documents.download'),
        onClick: () => handleAction(ACTION_IDS.DOWNLOAD, document),
      })

      // Move to trash
      if (canDelete && !document.folder?.showOnly) {
        actions.push({
          id: ACTION_IDS.MOVE_TO_TRASH,
          label: i18n.t('documents.move_to_trash_bin'),
          onClick: () => handleAction(ACTION_IDS.MOVE_TO_TRASH, document),
          critical: true,
        })
      }

      return actions
    },
    [isInternalSpace, handleAction]
  )

  const onSubmitTrash = useCallback(async () => {
    if (!trashModalDoc) return
    const result = await moveDocumentToTrashBin({ documentIds: [trashModalDoc.id] })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.removed_successfully') })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    setTrashModalDoc(null)
  }, [trashModalDoc, moveDocumentToTrashBin])

  const onSubmitFolder = useCallback(
    async (selectedFolderId: string | null) => {
      if (!folderModalDoc) return
      const result = await bulkUpdateFolder({
        documentIds: [folderModalDoc.id],
        folderId: selectedFolderId,
      })
      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('documents.employee.bulk-actions.update-folder.success'),
        })
      } else {
        app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
      }
      setFolderModalDoc(null)
    },
    [folderModalDoc, bulkUpdateFolder]
  )

  const ItemActionModals = useCallback(
    () => (
      <>
        {trashModalDoc && (
          <TrashBinModal
            filename={trashModalDoc.filename}
            onClose={() => setTrashModalDoc(null)}
            onSubmit={onSubmitTrash}
            loading={loadingTrashBin}
            hasSignature={trashModalDoc.signatureStatus !== null}
          />
        )}
        {folderModalDoc && (
          <UpdateFolderModal
            documents={[folderModalDoc]}
            onClose={() => setFolderModalDoc(null)}
            onSubmit={onSubmitFolder}
            loading={loadingUpdateFolder}
          />
        )}
      </>
    ),
    [
      trashModalDoc,
      folderModalDoc,
      onSubmitTrash,
      onSubmitFolder,
      loadingTrashBin,
      loadingUpdateFolder,
    ]
  )

  return {
    itemActions: getItemActions,
    ItemActionModals,
  }
}
